/**
 * Referral code capture for the web app.
 *
 * Flow:
 * 1. User lands on ANY route with ?r=CODE (from itsbaba.com/r/CODE
 *    redirect, shared link, email, etc.) → we persist the code to
 *    localStorage with a 30-day expiry so it survives tab close / reload.
 * 2. When they hit the sign-in modal and request a magic link, we read
 *    the code and pass it in the POST body. The Worker records a pending
 *    referral row keyed by their email.
 * 3. After the referred user verifies + upgrades, the Stripe webhook
 *    claims the reward for the referrer. No client-side claim needed.
 *
 * The extension has its own referral_code localStorage key on the
 * itsbaba.com origin (see apps/extension/content/content-script.js).
 * The web app uses the SAME key so a single landing page can drop the
 * code and either client reads it.
 */

const STORAGE_KEY = 'baba_referral_code';
const EXPIRY_KEY = 'baba_referral_code_expires_at';
const EXPIRY_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
const CODE_RE = /^[A-Z0-9]{6,16}$/;

function normalize(code: string | null | undefined): string | null {
  if (!code) return null;
  const up = String(code).toUpperCase().trim();
  return CODE_RE.test(up) ? up : null;
}

/**
 * Captures ?r=CODE from the current URL (if present) and persists it.
 * Safe to call on every route mount — no-op when no code or already stored.
 * Strips the `r` query param from the URL afterwards so it doesn't linger
 * in share links or analytics reports.
 */
export function captureReferralFromUrl(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const url = new URL(window.location.href);
    const fromUrl = normalize(url.searchParams.get('r'));
    if (!fromUrl) return getStoredReferralCode();

    localStorage.setItem(STORAGE_KEY, fromUrl);
    localStorage.setItem(EXPIRY_KEY, String(Date.now() + EXPIRY_MS));

    url.searchParams.delete('r');
    const clean = url.pathname + (url.search ? url.search : '') + url.hash;
    window.history.replaceState({}, '', clean);

    return fromUrl;
  } catch {
    return getStoredReferralCode();
  }
}

export function getStoredReferralCode(): string | null {
  if (typeof window === 'undefined') return null;
  try {
    const expiresAt = Number(localStorage.getItem(EXPIRY_KEY) || 0);
    if (expiresAt && Date.now() > expiresAt) {
      localStorage.removeItem(STORAGE_KEY);
      localStorage.removeItem(EXPIRY_KEY);
      return null;
    }
    return normalize(localStorage.getItem(STORAGE_KEY));
  } catch {
    return null;
  }
}

/**
 * Called after a successful magic-link send. The referral has now been
 * attributed server-side, so clear the localStorage to avoid re-attributing
 * the same code on a second signup from the same browser (e.g. family member).
 */
export function clearStoredReferralCode(): void {
  if (typeof window === 'undefined') return;
  try {
    localStorage.removeItem(STORAGE_KEY);
    localStorage.removeItem(EXPIRY_KEY);
  } catch {}
}
